import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ROUTE_BUILDERS } from '@/config/routes'
import { dmyToYmd, ymdToDmy } from './reportDateParams'
import { formatReportDate, parseReportDate } from './reportDateDisplay'
import { APPLICATION_STATUS_LABELS, CLAIM_STATUS_OPTIONS } from '../constants/configuredReportFilterOptions'

export function display(value: unknown): string {
  if (value == null || value === '') return '—'
  return String(value)
}

export function numberValue(value: unknown): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function formatDate(value: unknown): string {
  if (!value) return '—'
  const raw = String(value)
  const ymd = dmyToYmd(raw) || raw
  return formatReportDate(ymd)
}

export function formatDateTime(value: unknown): string {
  if (!value) return '—'
  const raw = String(value)
  const ymd = dmyToYmd(raw) || raw
  const d = parseReportDate(ymd)
  if (!d) return raw
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export function applicantId(value: unknown): string {
  if (value == null || value === '') return '—'
  return String(value).padStart(8, '0')
}

export function applicantLink(value: unknown): ReactNode {
  if (value == null || value === '') return '—'
  return makeLink(
    ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(String(value)),
    applicantId(value),
    true,
  )
}

export function fullName(...values: unknown[]): string {
  const name = values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ')
  return name || '—'
}

export function statusLabel(value: unknown): string {
  const key = String(value ?? '')
  return APPLICATION_STATUS_LABELS[key] ?? display(value)
}

export function reimbursementStatusLabel(value: unknown): string {
  const status = display(value)
  if (status === 'External Review') return 'Level 3 Review'
  if (status === '—') return status
  return (
    CLAIM_STATUS_OPTIONS.find(
      (option) => option.value === status || option.label === status,
    )?.label ?? status
  )
}

export function yesNo(value: unknown): string {
  return String(value ?? '') === '1' || value === true ? 'Yes' : 'No'
}

export function dateForFormat(value: unknown, format: 'DMY' | 'YMD'): string {
  const raw = String(value ?? '')
  if (!raw) return ''
  const ymd = dmyToYmd(raw) || raw
  return format === 'YMD' ? ymd : ymdToDmy(ymd) || raw
}

export function makeLink(to: string, label: ReactNode, newTab = false): ReactNode {
  return (
    <Link
      to={to}
      className="text-[#007BFF] hover:underline"
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
    >
      {label}
    </Link>
  )
}

